import React, { useCallback, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import ApiClient from '../../../services/api/ApiClient'
import Button from '../../ui/Button'
import Modal from '../../shared/Modal'
import Spinner from '../../ui/Spinner'
import PhotoRow from './PhotoRow'
import PhotoViewer, { buildViewerPhotos } from '../../ui/PhotoViewer'
import UploadIcon from '../../ui/icons/UploadIcon'

const SIGNED_ID = 'signed_id'
const SPHERICAL_PHOTO = 'spherical_photo'
const DOWNLOAD_URL = 'download_url'
const PROXY_URL = 'proxy_url'
const BYTE_SIZE = 'byte_size'
const CREATED_AT = 'created_at'
const ACCEPTED_TYPES = 'image/png,image/gif,image/jpeg,image/jpg'
// Mirrors the photo size validations in Mark.
const MAX_SPHERICAL_SIZE_MB = 20
const MAX_SIZE_MB = 10
const UPLOAD_CLASS = 'tw:ds-upload-dashed tw:flex tw:w-full tw:items-center tw:gap-3 tw:rounded-lg! '
  + 'tw:border-0 tw:bg-white tw:px-3 tw:py-4 tw:cursor-pointer '
  + 'tw:disabled:cursor-not-allowed tw:disabled:opacity-50'
const DELETE_ICON_CLASS = 'tw:flex tw:size-12 tw:items-center tw:justify-center tw:rounded-lg! '
  + 'tw:bg-ds-progress-lowest tw:text-2xl tw:text-ds-warning'

const PhotosMarkField = ({ mark, readonly, onChange }) => {
  const photos = mark.photos || []
  const maxSizeMb = mark.question[SPHERICAL_PHOTO] ? MAX_SPHERICAL_SIZE_MB : MAX_SIZE_MB
  const [uploading, setUploading] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [photoToDelete, setPhotoToDelete] = useState(null)
  const [photoToView, setPhotoToView] = useState(null)
  const [uploadError, setUploadError] = useState(null)
  const [deleteError, setDeleteError] = useState(null)
  const inputRef = useRef(null)

  const handleFiles = useCallback(async event => {
    const { files } = event.target
    if (files.length === 0)
      return

    const formData = new FormData()
    formData.append('mark_id', mark.id)
    Array.from(files).forEach(file => formData.append('photos[]', file, file.name))

    setUploading(true)
    setUploadError(null)
    try {
      const data = await ApiClient.postFormData('/attachments', formData)
      onChange({ photos: data.photos })
    } catch (error) {
      setUploadError(error.message)
    } finally {
      setUploading(false)
      if (inputRef.current)
        inputRef.current.value = ''
    }
  }, [mark.id, onChange])

  const openFilePicker = useCallback(() => inputRef.current.click(), [])

  const closeViewer = useCallback(() => setPhotoToView(null), [])
  const closeDeleteModal = useCallback(() => {
    setPhotoToDelete(null)
    setDeleteError(null)
  }, [])

  const confirmDelete = useCallback(async() => {
    if (!photoToDelete)
      return

    const signedId = photoToDelete[SIGNED_ID]
    setDeleting(true)
    setDeleteError(null)
    try {
      await ApiClient.delete(`/attachments/${signedId}`)
      onChange({ photos: photos.filter(photo => photo[SIGNED_ID] !== signedId) })
      setPhotoToDelete(null)
    } catch (error) {
      setDeleteError(error.message)
    } finally {
      setDeleting(false)
    }
  }, [photoToDelete, photos, onChange])

  const viewerPhotos = buildViewerPhotos(photos)
  const viewerIndex = photos.findIndex(photo => photo[SIGNED_ID] === photoToView?.[SIGNED_ID])

  const deleteFooter = (
    <>
      <Button variant="neutral" onClick={closeDeleteModal}>
        Annuler
      </Button>
      <Button disabled={deleting} variant="danger" onClick={confirmDelete}>
        Supprimer
      </Button>
    </>
  )

  const deleteHeader = (
    <div className="tw:flex tw:justify-end tw:px-4 tw:pt-3">
      <button aria-label="Fermer la modale" className="close" type="button" onClick={closeDeleteModal}>
        <i aria-hidden="true" className="fas fa-times" />
      </button>
    </div>
  )

  return (
    <div className="tw:flex tw:lg:w-1/2 tw:flex-col tw:gap-4">
      {readonly ? null : (
        <>
          <input
            ref={inputRef}
            accept={ACCEPTED_TYPES}
            className="tw:hidden"
            type="file"
            multiple
            onChange={handleFiles}
          />
          <button
            className={UPLOAD_CLASS}
            disabled={uploading}
            type="button"
            onClick={openFilePicker}
          >
            {uploading ? <Spinner size="xs" /> : (
              <span className="tw:text-xl tw:text-gray-400">
                <UploadIcon />
              </span>
            )}
            <span className="tw:flex tw:items-end tw:gap-2 tw:text-gray-500">
              <span className="tw:text-sm tw:font-medium">Ajouter un fichier</span>
              <span className="tw:text-xs tw:font-normal">{`(Max. ${maxSizeMb} Mo)`}</span>
            </span>
          </button>
        </>
      )}
      {uploadError ? (
        <div className="tw:text-sm tw:text-ds-danger">
          {uploadError}
        </div>
      ) : null}
      {photos.length ? (
        <div className="tw:flex tw:flex-col tw:gap-4">
          {photos.map(photo => (
            <PhotoRow
              key={photo[SIGNED_ID]}
              photo={photo}
              readonly={readonly}
              onDelete={setPhotoToDelete}
              onView={setPhotoToView}
            />
          ))}
        </div>
      ) : null}
      <PhotoViewer
        isOpen={Boolean(photoToView)}
        photos={viewerPhotos}
        startIndex={viewerIndex < 0 ? 0 : viewerIndex}
        onClose={closeViewer}
      />
      <Modal
        footer={deleteFooter}
        header={deleteHeader}
        isOpen={Boolean(photoToDelete)}
        size="md"
        title="Supprimer la photo"
        onClose={closeDeleteModal}
      >
        <div className="tw:flex tw:flex-col tw:items-center tw:gap-4 tw:pb-2 tw:text-center">
          <div className={DELETE_ICON_CLASS}>
            <i aria-hidden="true" className="fas fa-exclamation-triangle" />
          </div>
          <div className="tw:flex tw:flex-col tw:gap-3">
            <p className="tw:m-0! tw:text-base tw:font-medium tw:text-gray-900">
              Suppression de la photo
            </p>
            <p className="tw:m-0! tw:text-base tw:text-gray-500">
              Cette photo sera définitivement supprimée. Cette action est irréversible.
            </p>
          </div>
          {deleteError ? <p className="tw:m-0! tw:text-sm tw:text-ds-danger">{deleteError}</p> : null}
        </div>
      </Modal>
    </div>
  )
}

PhotosMarkField.propTypes = {
  mark: PropTypes.shape({
    id: PropTypes.number.isRequired,
    question: PropTypes.shape({ [SPHERICAL_PHOTO]: PropTypes.bool }).isRequired,
    photos: PropTypes.arrayOf(PropTypes.shape({
      [BYTE_SIZE]: PropTypes.number,
      [CREATED_AT]: PropTypes.string,
      [DOWNLOAD_URL]: PropTypes.string,
      filename: PropTypes.string,
      [PROXY_URL]: PropTypes.string,
      [SIGNED_ID]: PropTypes.string,
      [SPHERICAL_PHOTO]: PropTypes.bool,
      url: PropTypes.string
    }))
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  readonly: PropTypes.bool
}

PhotosMarkField.defaultProps = {
  readonly: false
}

export default PhotosMarkField
